<template>
  <div class="us-minute-chart">
    <div class="index-tabs">
      <button v-for="(item, index) in indexTabs" :key="item.code" :class="{ active: selectedIndex === index }" type="button" @click="select(index)">{{ item.name }}</button>
    </div>
    <div ref="chart" class="chart"></div>
    <div v-if="error" class="empty">{{ error }}</div>
  </div>
</template>

<script>
import { usMinutes } from '@/utils/usChartData'
import { pollMarket, marketRequest, MARKET_REFRESH_MS, publicBody } from '@/utils/marketRefresh'
import HQChart from 'hqchart'
import { applyChartPricePrecision } from '@/utils/chartPricePrecision'
import DefaultData from '@/utils/DefaultData'
import { applyChartTheme } from '@/utils/chartTheme'

export default {
  name: 'USMinuteChart',
  props: { indexes: { type: Array, default: () => [] }, active: { type: Boolean, default: true } },
  data() {
    return {
      selectedIndex: 0, chart: null, error: '', option: DefaultData.GetMinuteOption(),
      indexTabs: [
        { code: 'usDJI', name: '道琼斯指数' },
        { code: 'usIXIC', name: '纳斯达克指数' },
        { code: 'usINX', name: '标普500指数' }
      ]
    }
  },
  watch: {
    active(value) { if (value) this.$nextTick(this.activate); else this.stopPolling() },
    '$store.state.quoteColorMode'() { applyChartTheme(this.chart, this.$store.state.theme) },
    '$store.state.theme'(theme) { applyChartTheme(this.chart, theme) }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    this.$nextTick(this.activate)
  },
  beforeDestroy() {
    this.disposed = true
    this.stopPolling()
    window.removeEventListener('resize', this.resize)
    if (this.chart) this.chart.ChartDestroy()
  },
  methods: {
    stopPolling() { if (this.stopRefresh) this.stopRefresh(); this.stopRefresh = null },
    activate() {
      if (this.disposed || !this.active || this.stopRefresh) return
      if (this.chart) { this.resize(); this.requestMinute() }
      else this.resetChart()
      this.stopRefresh = pollMarket(() => this.requestMinute(), MARKET_REFRESH_MS, 'us')
    },
    requestMinute() {
      if (!this.disposed && this.active && this.chart) return this.chart.JSChartContainer.RequestMinuteData()
    },
    async networkFilter(data, callback) {
      data.PreventDefault = true
      if (data.Name !== 'MinuteChartContainer::RequestMinuteData') { callback({ code: 0, data: [] }); return }
      if (this.disposed || !this.active || document.hidden || this.minuteLoading) return
      this.minuteLoading = true
      const item = this.indexTabs[this.selectedIndex]
      try {
        const body = publicBody(await marketRequest('us-minute', { code: item.code }, !this.minuteLoaded))
        if (this.disposed || !this.active || data.Self.IsDestroy) return
        const stock = body && body.data && body.data[item.code]
        const points = usMinutes(stock && stock.data && stock.data.data)
        if (!points.length) throw new Error('No US minute points')
        this.minuteLoaded = true; this.error = ''
        const symbol = `${item.code.substring(2)}.USAI`
        callback({ code: 0, stock: [{ name: symbol, symbol, date: Number(stock.data.date), yclose: Number(stock.qt && stock.qt[item.code] && stock.qt[item.code][4]), minute: points }] })
      } catch (_) {
        if (!data.Self.IsDestroy && !this.disposed) { this.error = '美股分时暂时无法加载'; callback({ code: 0, stock: [] }) }
      } finally { this.minuteLoading = false }
    },
    select(index) {
      if (this.selectedIndex === index) return
      this.selectedIndex = index
      this.resetChart()
    },
    resetChart() {
      if (!this.$refs.chart) return
      if (this.chart) this.chart.ChartDestroy()
      this.$refs.chart.innerHTML = ''
      const item = this.indexTabs[this.selectedIndex]
      this.error = ''
      this.option = DefaultData.GetMinuteOption()
      // HQChart uses .USAI for US indexes and their trading hours.
      this.option.Symbol = `${item.code.substring(2)}.USAI`
      this.option.symbol = this.option.Symbol
      this.option.AutoUpdateFrequency = MARKET_REFRESH_MS
      this.option.IsAutoUpdate = false
      this.minuteLoaded = false
      this.option.NetworkFilter = this.networkFilter
      applyChartTheme(null, this.$store.state.theme)
      this.setChartSize()
      this.chart = HQChart.Chart.JSChart.Init(this.$refs.chart)
      this.chart.SetOption(this.option)
      applyChartPricePrecision(this.chart)
      this.resize()
    },
    resize() {
      this.setChartSize()
      if (this.chart) this.chart.OnSize()
    },
    setChartSize() {
      const element = this.$refs.chart
      if (element) {
        element.style.width = `${Math.max(element.parentElement.clientWidth - 25, 1)}px`
        element.style.height = `${Math.floor(window.innerHeight * 0.34495)}px`
      }
    },
  }
}
</script>
<style scoped>
.us-minute-chart { position: relative; width: 100%; }
.index-tabs { height: 36px; margin-bottom: 6px; background: var(--theme-header); display: flex; }
.index-tabs button { width: 120px; min-width: 0; padding: 0 4px; border: 0; background: transparent; color: var(--theme-label); cursor: pointer; }
.index-tabs button.active { color: var(--theme-text); border-bottom: 2px solid var(--theme-text); }
.chart { height: 34.495vh; background: var(--theme-bg); }
.empty { position: absolute; inset: 80px 0 auto; color: #9da6b3; text-align: center; }
</style>
